// Guest passwords for the Room Door Lock (DeviceID 102) — the three create
// screens a Tuya lock app offers, driven by OUR ESP instead of Tuya's cloud:
//
//   one-time      a generated code, valid ONE_TIME_HOURS from now, one use
//   time-limited  a generated code, valid between two date-times
//   scheduled     a code the owner types (or generates), valid between two
//                 date-times and, with Repeat on, only inside a daily window on
//                 the chosen weekdays
//
// ── Where a code lives (2026-09-29, aligned with the backend) ───────────────
// The SERVER keeps the list — Home Automation api/new/lockPasswords.js, table
// LockPassword: one row per code, in a numbered SLOT 0..99 that the lock
// registers as 900 + slot. Every code is EXACTLY 7 DIGITS (the lock's keypad
// format; the server rejects anything else). So a code is:
//
//   1. saved on the server  (SaveLockPassword)  -> its slot id
//   2. sent to the lock      (lockSetCommand)    -> the lock's reply
//
// and deleted the same way round (DeleteLockPassword, then lockDeleteCommand).
// The lock stores each code and checks it at its own keypad, so once it has
// RECEIVED a code it works with no network at the door.
//
// ⚠️ THE LOCK COMMAND WORDS ARE PROVISIONAL. The firmware's wording for adding
// and deleting a code has not been handed over (2026-09-29). This file is the
// ONLY place that encodes them: change LOCK_CMD / lockSetCommand /
// lockDeleteCommand / parseLockReply when they arrive, and nothing in ui/ moves.
//
// ⚠️ A PUBLISH IS NOT A SAVE. Until the lock answers LOCK_REPLY.ok the code is
// not on the lock. The screens show the code with the lock's answer — waiting,
// confirmed, refused, or silent — never a bare "Saved".

import { post } from '../api/api';

export const ROOM_DOOR_LOCK_DEVICE_ID = 102;
export const supportsLockPasswords = (deviceId) =>
  Number(deviceId) === ROOM_DOOR_LOCK_DEVICE_ID;

export const PASSWORD_KIND = {
  ONE_TIME: 'oneTime',
  TIME_LIMITED: 'timeLimited',
  SCHEDULED: 'scheduled',
};

export const ONE_TIME_HOURS = 6;
export const CODE_LENGTH = 7;          // the lock's keypad format — server-enforced
// Kept as names the screens already use; both are the one lock format now.
export const GENERATED_LENGTH = CODE_LENGTH;
export const SCHEDULED_LENGTH = CODE_LENGTH;
export const MAX_SLOTS = 100;
export const REPLY_TIMEOUT_MS = 15000;

// PROVISIONAL — replace with the firmware's words.
export const LOCK_CMD = { set: 'PWDSET', del: 'PWDDEL' };
// PROVISIONAL — what the lock sends back on `<uno>/client` (between ] and #).
export const LOCK_REPLY = { ok: 'PWDOK', fail: 'PWDERR' };

// Sun = bit 0 … Sat = bit 6, matching Date#getDay().
export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const ALL_DAYS_MASK = 0x7f;

export const daysToMask = (days) => days.reduce((m, d) => m | (1 << d), 0);
export const maskToDays = (mask) => WEEKDAYS.map((_, i) => i).filter((i) => mask & (1 << i));

// A uniformly random digit string. Rejection sampling, because `byte % 10`
// makes 0–5 more likely than 6–9 — a small bias, but this is a door code.
export function randomDigits(length = CODE_LENGTH, rng = defaultRng) {
  let out = '';
  while (out.length < length) {
    const bytes = rng(length * 2);
    for (let i = 0; i < bytes.length && out.length < length; i += 1) {
      if (bytes[i] < 250) out += String(bytes[i] % 10);
    }
  }
  return out;
}

function defaultRng(n) {
  const a = new Uint8Array(n);
  window.crypto.getRandomValues(a);
  return a;
}

// Epoch SECONDS (UTC). The ESP keeps NTP time, so an absolute instant needs no
// time zone. The daily window below is different — it is local wall-clock.
export const toEpoch = (date) => Math.floor(date.getTime() / 1000);

const hhmm = (s) => String(s || '').replace(':', '');
const split = (s) => {
  const [h, m] = String(s || '00:00').split(':').map((v) => Number(v) || 0);
  return [h, m];
};
const pad2 = (n) => String(n).padStart(2, '0');

/**
 * A screen's password -> the server's SaveLockPassword body.
 * `p` = { kind, code, from, until, repeat, startTime, endTime, dayMask, name }.
 */
export function toServerRow(p, uniqueNumber, userId) {
  const repeat = p.kind === PASSWORD_KIND.SCHEDULED && p.repeat;
  const [sh, sm] = repeat ? split(p.startTime) : [0, 0];
  const [eh, em] = repeat ? split(p.endTime) : [23, 59];
  return {
    UniqueNumber: String(uniqueNumber),
    code: String(p.code),
    oneTime: p.kind === PASSWORD_KIND.ONE_TIME ? 1 : 0,
    startEpoch: toEpoch(p.from),
    endEpoch: toEpoch(p.until),
    allDay: repeat ? 0 : 1,
    days: repeat ? p.dayMask : ALL_DAYS_MASK,
    startHH: sh, startMM: sm, endHH: eh, endMM: em,
    Label: String(p.name || '').trim() || null,
    UserId: Number(userId) || undefined,
  };
}

/**
 * The lock command for a code in its slot. PROVISIONAL format:
 *
 *   PWDSET:<slot>,<code>,<startEpoch>,<endEpoch>,<oneTime>,<allDay>,<days>,<HHMM>,<HHMM>
 *
 * `row` is the server's shape (toServerRow / GetLockPasswords). The slot is the
 * server's 0..99 id — the firmware adds 900. ⚠️ HH:MM are LOCAL wall-clock (the
 * lock compares them in IST); the epochs are UTC.
 */
export function lockSetCommand(slot, row) {
  const d = row;
  return `${LOCK_CMD.set}:${Number(slot)},${d.code},${Number(d.startEpoch) || 0},${Number(d.endEpoch) || 0},`
    + `${d.oneTime ? 1 : 0},${d.allDay ? 1 : 0},${Number(d.days ?? ALL_DAYS_MASK)},`
    + `${pad2(d.startHH || 0)}${pad2(d.startMM || 0)},${pad2(d.endHH ?? 23)}${pad2(d.endMM ?? 59)}`;
}

/** PROVISIONAL: `PWDDEL:<slot>`. */
export const lockDeleteCommand = (slot) => `${LOCK_CMD.del}:${Number(slot)}`;

// -> 'ok' | 'fail' | null (not a password reply at all).
export function parseLockReply(status) {
  const s = String(status || '').trim().toUpperCase();
  if (s.startsWith(LOCK_REPLY.ok)) return 'ok';
  if (s.startsWith(LOCK_REPLY.fail)) return 'fail';
  return null;
}

// -> an error message, or null when the password can be sent.
export function validatePassword(p, now = new Date()) {
  if (!new RegExp(`^\\d{${CODE_LENGTH}}$`).test(String(p.code || ''))) {
    return `The password must be exactly ${CODE_LENGTH} digits.`;
  }
  if (!(p.from instanceof Date) || Number.isNaN(p.from.getTime())) return 'Choose the effective time.';
  if (!(p.until instanceof Date) || Number.isNaN(p.until.getTime())) return 'Choose the expiration time.';
  if (p.until <= p.from) return 'The expiration time must be after the effective time.';
  if (p.until <= now) return 'The expiration time is already in the past.';
  if (p.kind === PASSWORD_KIND.SCHEDULED && p.repeat) {
    if (!p.startTime || !p.endTime) return 'Choose the start and end time.';
    if (hhmm(p.endTime) <= hhmm(p.startTime)) return 'The end time must be after the start time.';
    if (!p.dayMask) return 'Choose at least one day.';
  }
  return null;
}

export function oneTimeWindow(now = new Date()) {
  return { from: now, until: new Date(now.getTime() + ONE_TIME_HOURS * 3600 * 1000) };
}

// ── The list — as the server returns it (GetLockPasswords) ──────────────────

/** Which of the three screens a stored row came from. */
export function kindOf(row) {
  if (Number(row.oneTime)) return PASSWORD_KIND.ONE_TIME;
  if (!Number(row.allDay) || Number(row.days) !== ALL_DAYS_MASK) return PASSWORD_KIND.SCHEDULED;
  return PASSWORD_KIND.TIME_LIMITED;
}

export const KIND_LABEL = {
  [PASSWORD_KIND.ONE_TIME]: 'One-time',
  [PASSWORD_KIND.TIME_LIMITED]: 'Time-limited',
  [PASSWORD_KIND.SCHEDULED]: 'Scheduled',
};

/** 'active' | 'upcoming' | 'expired' for a stored row, at `now`. */
export function stateOf(row, now = new Date()) {
  const t = toEpoch(now);
  const s = Number(row.startEpoch) || 0;
  const e = Number(row.endEpoch) || 0;
  if (e && t > e) return 'expired';
  if (s && t < s) return 'upcoming';
  return 'active';
}

/** "Mon, Wed 08:00–09:00" for a scheduled row, '' otherwise. */
export function windowText(row) {
  if (Number(row.allDay) && Number(row.days) === ALL_DAYS_MASK) return '';
  const days = Number(row.days) === ALL_DAYS_MASK
    ? 'Every day'
    : maskToDays(Number(row.days)).map((d) => WEEKDAYS[d]).join(', ');
  const hours = Number(row.allDay)
    ? ''
    : ` ${pad2(row.startHH)}:${pad2(row.startMM)}–${pad2(row.endHH)}:${pad2(row.endMM)}`;
  return `${days}${hours}`;
}

// ── Server calls ─────────────────────────────────────────────────────────────
const data = (res) => (res && res.data && typeof res.data === 'object' ? res.data : null);

/** -> rows (server shape: id, code, oneTime, startEpoch, …) or null when unavailable. */
export async function getLockPasswords(uniqueNumber) {
  try {
    const d = data(await post('GetLockPasswords', { UniqueNumber: String(uniqueNumber) }));
    return d && Array.isArray(d.passwords) ? d.passwords : null;
  } catch (e) {
    return null;
  }
}

/** -> { ok, id } or { ok:false, message } — the server's own refusal text when it gives one. */
export async function saveLockPassword(row) {
  try {
    const d = data(await post('SaveLockPassword', row));
    if (d && d.ok) return { ok: true, id: Number(d.id) };
    return { ok: false, message: (d && d.message) || 'The server did not save the password.' };
  } catch (e) {
    const d = e && e.response && e.response.data;
    return { ok: false, message: (d && d.message) || 'Could not reach the server.' };
  }
}

export async function deleteLockPassword(uniqueNumber, id) {
  try {
    const d = data(await post('DeleteLockPassword', { UniqueNumber: String(uniqueNumber), id: Number(id) }));
    return !!(d && d.ok);
  } catch (e) {
    return false;
  }
}

/** The lock's stored state (battery while it sleeps) — lockEvents.js on the server. */
export async function getLockState(uniqueNumber) {
  try {
    const d = data(await post('GetLockState', { UniqueNumber: String(uniqueNumber) }));
    return d && d.ok ? d : null;
  } catch (e) {
    return null;
  }
}

/**
 * Open the Room Door Lock THROUGH THE SERVER — POST /api/OpenRoomDoorLock
 * (Home Automation api/index.js). The server checks the user is an ACTIVE mapped
 * user of this lock and that it is a 102, then publishes LOCKUNLOCK. Its author:
 * "keep remote opens flowing through this authenticated endpoint".
 *
 * -> 'sent'        the server published LOCKUNLOCK
 *    'refused'     the server said no (403 not mapped / 400 not a 102) — FINAL,
 *                  the caller must NOT fall back to publishing it itself
 *    'unavailable' no answer / endpoint absent — the caller may publish directly
 */
export async function openRoomDoorLockViaServer(uniqueNumber, userId) {
  try {
    const d = data(await post('OpenRoomDoorLock', { UniqueNumber: String(uniqueNumber), UserId: Number(userId) }));
    if (d && d.ok) return 'sent';
    return d && d.ok === false ? 'refused' : 'unavailable';
  } catch (e) {
    const status = e && e.response && e.response.status;
    const d = e && e.response && e.response.data;
    if ((status === 403 || status === 400) && d && typeof d === 'object') return 'refused';
    return 'unavailable';
  }
}
